import { DOMAIN, urlParam } from "../config";
import { deliveryCountry } from "./visitor-country";

//India PIN code -> city and state. A 6-digit Indian PIN maps to a single
//district, so the fill is a safe guess: both fields stay editable, nothing is
//locked, and any failure or timeout is silent, leaving manual entry exactly as
//it was.
const PATH = "/funnels-sdk/pincode/";
const TIMEOUT_MS = 4000;
const DEBOUNCE_MS = 400;

const cache = new Map();

export function pincodeAutofill() {
	document
		.querySelectorAll('input[name="zip_code"], input[id="zip_code"]')
		.forEach(bind);
}

function bind(pinInput) {
	//Fields are found by name within the owning form, not by id: several
	//landers repeat the whole checkout on one page, which makes the ids
	//duplicates that getElementById can never reach past the first.
	const scope = pinInput.form || document;
	const cityInput = fieldIn(scope, "input", "city");
	const stateSelect = fieldIn(scope, "select", "state");

	if (!cityInput && !stateSelect) {
		return;
	}

	let lastPin = "";
	let timer = null;

	const maybeLookup = () => {
		const pin = String(pinInput.value || "").replace(/\D/g, "");

		if (pin.length !== 6 || pin === lastPin) {
			return;
		}
		//Only India has this mapping, so follow the delivery country the
		//customer is ordering to - not where they happen to be browsing from,
		//which says nothing about the address being typed.
		if (deliveryCountry(pinInput) !== "IN") {
			return;
		}

		lastPin = pin;
		lookup(pin).then((result) => {
			if (!result) {
				return;
			}
			setCity(cityInput, result.city);
			setState(stateSelect, result.state);
		});
	};

	pinInput.addEventListener("input", () => {
		clearTimeout(timer);
		timer = setTimeout(maybeLookup, DEBOUNCE_MS);
	});
	pinInput.addEventListener("blur", maybeLookup);
}

//Prefer the name attribute so repeated forms each resolve their own field,
//and fall back to the id for any markup that only carries one.
function fieldIn(scope, selector, name) {
	return (
		scope.querySelector(selector + '[name="' + name + '"]') ||
		scope.querySelector(selector + '[id="' + name + '"]')
	);
}

function lookup(pin) {
	if (cache.has(pin)) {
		return Promise.resolve(cache.get(pin));
	}

	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

	//The transaction scopes the lookup to an India-shipping offer server-side;
	//a checkout reached without one sends it empty and the server answers on
	//the PIN alone.
	const url =
		DOMAIN +
		PATH +
		pin +
		"?transaction_id=" +
		encodeURIComponent(urlParam("transaction_id"));

	return fetch(url, { signal: controller.signal })
		.then((response) => response.json())
		.then((data) => {
			clearTimeout(timer);
			if (!data || !data.ok || !data.city || !data.state) {
				return null;
			}
			const result = { city: data.city, state: data.state };
			cache.set(pin, result);
			return result;
		})
		.catch(() => {
			clearTimeout(timer);
			return null;
		});
}

function setCity(input, city) {
	if (!input || !city) {
		return;
	}
	input.value = city;
	announce(input, "input");
	announce(input, "change");
}

function setState(select, stateName) {
	if (!select || !stateName || !select.options) {
		return;
	}

	const target = String(stateName).trim().toLowerCase();
	const option = Array.from(select.options).find(
		(o) =>
			String(o.value).trim().toLowerCase() === target ||
			String(o.text).trim().toLowerCase() === target,
	);

	//No matching option: leave whatever the visitor picked.
	if (option) {
		select.value = option.value;
		announce(select, "change");
	}
}

//Let the page's own validation and any framework listeners see the fill.
function announce(element, type) {
	element.dispatchEvent(new Event(type, { bubbles: true }));
}
